import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Sparkles, TrendingUp, AlertCircle, ChevronRight, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Widget "Consumo Pando acumulado del mes" — sólo visible para admin.
 * Consume GET /api/admin/ai-usage/summary y muestra: coste total, %/budget,
 * top 3 usuarios, breakdown por modelo, y estado de budget.
 */
const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AIUsageWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);

  const sendWeeklyEmail = async () => {
    setSending(true);
    try {
      const { data: res } = await axios.post(`${API}/admin/ai-usage/send-weekly-email`);
      if (res.sent) {
        toast.success(`Email enviado a ${res.recipient}`, {
          description: `Semana: $${Number(res.week_cost_usd).toFixed(4)} · Mes: $${Number(res.month_cost_usd).toFixed(4)} · ${res.budget_status?.toUpperCase()}`,
        });
      } else {
        toast.error("No se pudo enviar el email", { description: res.error || "Sin detalle" });
      }
    } catch (e) {
      toast.error("Error enviando email", { description: e.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/ai-usage/summary`);
        setData(data);
      } catch (e) {
        const status = e.response?.status;
        // 403 = non-admin → hide silently (esperado, no es error)
        // Otros errores (500, network) → mostrar mensaje al admin
        if (status !== 403) {
          setErr(e.response?.data?.detail || e.message || "Error consultando consumo Pando");
        }
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-100 p-4 flex items-center justify-center h-40" data-testid="ai-usage-widget-loading">
        <Loader2 size={20} className="animate-spin text-indigo-500" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-rose-50 rounded-lg border border-rose-200 p-4" data-testid="ai-usage-widget-error">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle size={14} className="text-rose-600" />
          <p className="text-xs font-bold text-rose-800">Widget Consumo Pando · error</p>
        </div>
        <p className="text-[11px] text-rose-700 mb-2">{err}</p>
        <button
          data-testid="ai-usage-widget-retry"
          onClick={() => { setErr(null); setLoading(true); axios.get(`${API}/admin/ai-usage/summary`).then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || e.message)).finally(() => setLoading(false)); }}
          className="text-[10px] font-semibold text-rose-700 hover:text-rose-900 underline"
        >
          Reintentar
        </button>
      </div>
    );
  }
  if (!data) return null;

  const p = data?.period || {};
  const t = data?.totals || {};
  const pct = t.cost_pct_of_budget || 0;
  const statusMap = {
    ok:       { chip: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "En budget", bar: "bg-emerald-500" },
    warning:  { chip: "bg-amber-100 text-amber-800 border-amber-200",       label: "Warning",   bar: "bg-amber-500" },
    exceeded: { chip: "bg-rose-100 text-rose-800 border-rose-200",           label: "EXCEDIDO",  bar: "bg-rose-500" },
  };
  const st = statusMap[p.budget_status] || statusMap.ok;

  return (
    <div className="bg-white rounded-lg border border-gray-100 p-4" data-testid="ai-usage-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-indigo-600" />
          <p className="text-xs font-bold text-gray-800 font-['Outfit']">
            Consumo Pando · {p.month || "—"}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="ai-usage-send-weekly-email-btn"
            onClick={sendWeeklyEmail}
            disabled={sending}
            title="Enviar resumen semanal por email al admin (auto lunes 09:00 UTC)"
            className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-semibold hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {sending
              ? <Loader2 size={9} className="animate-spin" />
              : <Mail size={9} />}
            {sending ? "Enviando…" : "Enviar ahora"}
          </button>
          <span
            data-testid="ai-usage-budget-status"
            className={`text-[9px] px-1.5 py-0.5 rounded font-semibold border ${st.chip}`}
            title={`Budget mensual: $${p.budget_usd?.toFixed(2)} USD`}
          >
            {st.label}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <StatBig testid="ai-usage-total-cost" icon={<TrendingUp size={12}/>} label="Coste" val={fmtUsd(t.cost_usd)} accent="indigo" />
        <StatBig testid="ai-usage-calls" label="Análisis" val={t.calls?.toLocaleString("es-ES") || 0} accent="gray" />
        <StatBig testid="ai-usage-tokens" label="Tokens IN" val={Math.round((t.tokens_in || 0) / 1000).toLocaleString("es-ES") + "K"} accent="gray" />
      </div>

      {/* Progress bar de budget */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
          <span>Budget mensual (${p.budget_usd?.toFixed(0)})</span>
          <span className="font-mono font-bold text-gray-700" data-testid="ai-usage-pct">{pct}%</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
          <div
            className={`h-full ${st.bar} transition-all`}
            style={{ width: `${Math.min(100, pct)}%` }}
            data-testid="ai-usage-progress-bar"
          />
        </div>
      </div>

      {/* Top usuarios (max 3) */}
      {(data?.by_user?.length ?? 0) > 0 && (
        <div className="mb-3">
          <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Top usuarios</p>
          <div className="space-y-0.5">
            {data.by_user.slice(0, 3).map((u) => (
              <div key={u.email} className="flex items-center justify-between text-[11px]" data-testid={`ai-usage-user-${u.email.split('@')[0]}`}>
                <span className="text-gray-700 truncate max-w-[60%]" title={u.email}>{u.name || u.email}</span>
                <span className="font-mono font-semibold text-indigo-700">{fmtUsd(u.cost_usd)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modelos */}
      {(data?.by_model?.length ?? 0) > 0 && (
        <div className="border-t border-gray-100 pt-2 flex flex-wrap gap-1">
          {data.by_model.map((m) => (
            <span key={m.model} className="text-[9px] px-1.5 py-0.5 rounded bg-gray-50 border border-gray-200 text-gray-700" title={`${m.calls} análisis`}>
              {m.model.replace("claude-", "")} · {fmtUsd(m.cost_usd)}
            </span>
          ))}
        </div>
      )}

      {p.budget_status === "exceeded" && (
        <div className="mt-2 flex items-start gap-1.5 bg-rose-50 border border-rose-200 rounded p-2 text-[10px] text-rose-800">
          <AlertCircle size={11} className="mt-0.5 flex-shrink-0" />
          <span>Budget mensual superado ({pct}%). Revisa el consumo por usuario.</span>
        </div>
      )}
    </div>
  );
}

function StatBig({ testid, icon, label, val, accent = "gray" }) {
  const cls = { indigo: "text-indigo-700", gray: "text-gray-800" }[accent] || "text-gray-800";
  return (
    <div className="bg-gray-50 rounded p-1.5">
      <div className="flex items-center gap-1 text-[9px] uppercase text-gray-500 font-semibold">
        {icon} {label}
      </div>
      <div className={`text-sm font-bold ${cls} mt-0.5`} data-testid={testid}>{val}</div>
    </div>
  );
}
